import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import "./AddFAQ.css";
import { useDispatch, useSelector } from "react-redux";
import { addFAQ } from "../../Redux/Actions/faqsAction";
import { getEnums } from "../../Redux/Actions/enumsActions";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const AddFAQ = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { enumsData = {} } = useSelector((state) => state.enumsData || {});
  const { loading = false } = useSelector((state) => state.faqs || {});

  const [formData, setFormData] = useState({
    page: "",
    question: "",
    answer: "",
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    dispatch(getEnums());
  }, [dispatch]);

  const pageOptions = useMemo(() => {
    return enumsData?.data?.pageName
      ? Object.entries(enumsData.data.pageName)
      : [];
  }, [enumsData]);

  const isGeneralQueries = useMemo(() => {
    const selectedValue = String(formData.page || "").toLowerCase();
    return (
      selectedValue === "general_queries" ||
      selectedValue === "general queries" ||
      selectedValue === "generalquery" ||
      selectedValue === "generalqueries"
    );
  }, [formData.page]);

  const clearError = (field) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "page") {
        const nextValue = String(value || "").toLowerCase();
        const isGeneral =
          nextValue === "general_queries" ||
          nextValue === "general queries" ||
          nextValue === "generalquery" ||
          nextValue === "generalqueries";

        if (isGeneral) {
          updated.answer = "";
        }
      }

      return updated;
    });

    clearError(name);

    if (name === "page") {
      clearError("answer");
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.page) {
      newErrors.page = "Please select a page";
    }

    if (!formData.question.trim()) {
      newErrors.question = "Question is required";
    }

    if (!isGeneralQueries && !formData.answer.trim()) {
      newErrors.answer = "Answer is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleReset = () => {
    setFormData({
      page: "",
      question: "",
      answer: "",
    });
    setErrors({});
    setSubmitted(false);
  };

  const handleFAQSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!validate()) return;

    try {
      const payload = {
        page: formData.page,
        question: formData.question.trim(),
      };

      if (!isGeneralQueries) {
        payload.answer = formData.answer.trim();
      }

      const response = await dispatch(addFAQ(payload));

      if (response?.statusCode === 200 || response?.success) {
        toast.success("FAQ added successfully");
        handleReset();
        navigate("/faq");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to add FAQ";

      toast.error(errorMessage);
    }
  };

  const showError = (field) => submitted && errors[field];

  return (
    <MainLayout>
      <div className="af-wrapper">
        <div className="af-header">
          <div className="af-header-left">
            <div className="af-header-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>

            <div>
              <h1 className="af-title">Add FAQ</h1>
              <p className="af-subtitle">
                Create a new frequently asked question
              </p>
            </div>
          </div>

          <button
            type="button"
            className="af-view-btn"
            onClick={() => navigate("/faq")}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
            View FAQ List
          </button>
        </div>

        <form className="af-card" onSubmit={handleFAQSubmit}>
          <div className="af-section-head">
            <span className="af-section-badge">FAQ</span>
            <h2 className="af-section-title">FAQ Details</h2>
          </div>

          <div className="af-field">
            <label className="af-label" htmlFor="af-page">
              Page <span className="af-req">*</span>
            </label>

            <div
              className={`af-select-wrap ${
                showError("page") ? "af-input--error" : ""
              }`}
            >
              <select
                id="af-page"
                name="page"
                value={formData.page}
                onChange={handleChange}
                className="af-select"
                disabled={loading}
              >
                <option value="">Select a page…</option>

                {pageOptions.length > 0 ? (
                  pageOptions.map(([key, value]) => (
                    <option key={key} value={value}>
                      {key}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    Loading options…
                  </option>
                )}
              </select>

              <svg
                className="af-select-arrow"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            {showError("page") && (
              <span className="af-error-msg">{errors.page}</span>
            )}
          </div>

          <div className="af-divider" />

          <div className="af-field">
            <label className="af-label" htmlFor="af-question">
              Question <span className="af-req">*</span>
            </label>

            <div className="af-textarea-wrap">
              <svg
                className="af-textarea-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>

              <textarea
                id="af-question"
                name="question"
                className={`af-textarea ${
                  showError("question") ? "af-input--error" : ""
                }`}
                value={formData.question}
                onChange={handleChange}
                rows={4}
                placeholder="Type your question here…"
                disabled={loading}
              />
            </div>

            {showError("question") && (
              <span className="af-error-msg">{errors.question}</span>
            )}
          </div>

          {!isGeneralQueries && (
            <>
              <div className="af-divider" />

              <div className="af-field">
                <label className="af-label" htmlFor="af-answer">
                  Answer <span className="af-req">*</span>
                </label>

                <div className="af-textarea-wrap">
                  <svg
                    className="af-textarea-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>

                  <textarea
                    id="af-answer"
                    name="answer"
                    className={`af-textarea ${
                      showError("answer") ? "af-input--error" : ""
                    }`}
                    value={formData.answer}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Type your answer here…"
                    disabled={loading}
                  />
                </div>

                {showError("answer") && (
                  <span className="af-error-msg">{errors.answer}</span>
                )}
              </div>
            </>
          )}

          {isGeneralQueries && (
            <>
              <div className="af-divider" />
              <div className="af-info-box">
                <span className="af-info-badge">Optional rule</span>
                <p className="af-info-text">
                  For General Queries, the answer field is not required.
                </p>
              </div>
            </>
          )}

          <div className="af-footer">
            <button
              type="button"
              className="af-btn af-btn--cancel"
              onClick={() => navigate("/faq")}
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="af-btn af-btn--submit"
              disabled={loading}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {loading ? "Submitting..." : "Submit FAQ"}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default AddFAQ;